import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, X, RefreshCw, Check, Trash2, Wand2, Image as ImageIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import ModalPortal from '../ModalPortal';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AIImageGenerateModal — reusable AI image generation UI.
 *
 * Props
 *   contextType  – "event" | "blog" | "newsletter" | "campaign" | "freeform"
 *   contextId    – optional id of a saved entity (we hydrate from DB)
 *   contextData  – optional unsaved draft payload (merged on top of DB hydrate)
 *   contextLabel – short label shown in the header (e.g. "Networking Gala")
 *   existingImageUrl – if the parent already has an image, we ask for
 *                      explicit confirmation before letting the user replace it
 *   onImageSelected(url, generationId) – fired when the user clicks "Use image".
 *                                        Parent is responsible for persisting.
 *   onClose() – fired on cancel or after Use
 *   freePromptHint – optional default text for the "additional instructions"
 *                    field (used by the Compose panel)
 */
const AIImageGenerateModal = ({
  contextType,
  contextId,
  contextData,
  contextLabel,
  existingImageUrl,
  onImageSelected,
  onClose,
  freePromptHint,
}) => {
  const [extra, setExtra] = useState(freePromptHint || '');
  const [freePrompt, setFreePrompt] = useState(freePromptHint || '');
  const [generating, setGenerating] = useState(false);
  const [generation, setGeneration] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(!existingImageUrl);
  useBodyScrollLock(true);

  const isFreeform = contextType === 'freeform';

  const buildBody = () => {
    if (isFreeform) {
      return {
        context_type: 'freeform',
        free_prompt: freePrompt.trim(),
      };
    }
    const body = {
      context_type: contextType,
      extra_instructions: extra.trim(),
    };
    if (contextId) body.context_id = contextId;
    if (contextData) body.context_data = contextData;
    return body;
  };

  const generate = async () => {
    if (generating) return;
    if (isFreeform && freePrompt.trim().length < 5) {
      toast.error('Describe what you want to see — at least a few words.');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/ai-image/generate`,
        buildBody(),
        { withCredentials: true, timeout: 90000 },
      );
      setGeneration(data);
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === 403) {
        toast.error('You do not have permission to generate images for this surface.');
      } else if (code === 400) {
        toast.error(detail || 'Please provide a valid prompt or saved draft.');
      } else if (code === 502) {
        toast.error('AI image generation failed — try again or add more instructions.');
      } else {
        toast.error('Could not generate image — please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const useImage = async () => {
    if (!generation) return;
    try {
      // Fire-and-forget attach record (non-blocking for the user).
      if (contextId) {
        axios.post(
          `${API}/admin/ai-image/attach`,
          {
            generation_id: generation.generation_id,
            context_type: contextType,
            context_id: contextId,
          },
          { withCredentials: true },
        ).catch(() => { /* silent — audit record is best-effort */ });
      }
      onImageSelected?.(generation.url, generation.generation_id);
      onClose?.();
    } catch {
      /* already handled */
    }
  };

  const discard = () => {
    setGeneration(null);
  };

  const contextLabelMap = {
    event:      'event',
    blog:       'article',
    newsletter: 'newsletter',
    campaign:   'campaign',
    freeform:   'image',
  };
  const noun = contextLabelMap[contextType] || 'image';

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="ai-image-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#002249] to-[#0a3570] text-white px-6 py-5 rounded-t-2xl flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-80">
                <Sparkles className="w-3.5 h-3.5" /> AI Image Generation
              </div>
              <h3 className="font-bold text-lg mt-1">
                Generate {isFreeform ? 'an image' : (contextType === 'event' ? 'an event image' : `a ${noun} image`)}
              </h3>
              {contextLabel && (
                <div className="text-xs opacity-80 mt-0.5 truncate max-w-md">
                  for <span className="font-semibold">{contextLabel}</span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white"
              data-testid="ai-image-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Existing image warning */}
            {existingImageUrl && !confirmReplace && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="font-semibold mb-1">This {noun} already has an image.</div>
                <p className="text-xs text-amber-800 mb-3">
                  Generating a new one won't overwrite anything until you click
                  "Use image" at the end. Continue?
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white"
                  onClick={() => setConfirmReplace(true)}
                  data-testid="ai-image-confirm-replace"
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Prompt field */}
            {confirmReplace && (
              <>
                {isFreeform ? (
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Describe the image
                    </label>
                    <textarea
                      value={freePrompt}
                      onChange={(e) => setFreePrompt(e.target.value)}
                      placeholder="e.g. Elegant Dominican-American networking event, waterfront venue, warm sunset lighting"
                      rows={3}
                      className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-[#002249] focus:outline-none"
                      data-testid="ai-image-free-prompt"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      We automatically add DCCF brand context — you don't need to mention it.
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Additional instructions <span className="font-normal normal-case text-slate-400">(optional)</span>
                    </label>
                    <textarea
                      value={extra}
                      onChange={(e) => setExtra(e.target.value)}
                      placeholder={`e.g. "Elegant Dominican-American business networking event, modern and professional."`}
                      rows={3}
                      className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-[#002249] focus:outline-none"
                      data-testid="ai-image-extra"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      The AI already reads this {noun}'s title, description and
                      Chamber context. Only add instructions if you want to
                      steer the mood or style.
                    </p>
                  </div>
                )}

                {/* Generate / Regenerate */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={generate}
                    disabled={generating}
                    className="bg-[#002249] hover:bg-[#0a3570] flex-1"
                    data-testid="ai-image-generate"
                  >
                    {generating ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating…</>
                    ) : generation ? (
                      <><RefreshCw className="w-4 h-4 mr-2" /> Regenerate</>
                    ) : (
                      <><Wand2 className="w-4 h-4 mr-2" /> Generate with AI</>
                    )}
                  </Button>
                </div>

                {/* Preview */}
                {generating && !generation && (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 h-64 flex items-center justify-center text-sm text-slate-400">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-6 h-6 animate-spin" />
                      Generating — this usually takes 10-20 seconds.
                    </div>
                  </div>
                )}

                {generation && (
                  <div className="space-y-3" data-testid="ai-image-preview">
                    <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${generation.url}`}
                        alt="AI generated preview"
                        className="w-full h-auto block"
                      />
                    </div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                      <ImageIcon className="w-3 h-3" />
                      Generated with {generation.provider} · {generation.mime_type}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        onClick={useImage}
                        className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                        data-testid="ai-image-use"
                      >
                        <Check className="w-4 h-4 mr-1.5" /> Use image
                      </Button>
                      <Button
                        onClick={discard}
                        variant="outline"
                        className="text-rose-600 border-rose-200 hover:bg-rose-50"
                        data-testid="ai-image-discard"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" /> Discard
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </ModalPortal>
  );
};

export default AIImageGenerateModal;
